/**
 * Leviathan Web - Tax Rates Management Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Tax Rates list with overview stats
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Receipt,
  Plus,
  Search,
  Edit,
  Trash2,
  DollarSign,
  MapPin,
  Calendar,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface TaxRate {
  id: string;
  name: string;
  code: string;
  description: string | null;
  taxType: string;
  rateType: string;
  rate: number;
  minAmount: number | null;
  maxAmount: number | null;
  jurisdiction: string | null;
  country: string;
  state: string | null;
  county: string | null;
  city: string | null;
  zipCodes: string[];
  productCategories: string[];
  effectiveDate: string;
  expirationDate: string | null;
  isActive: boolean;
  isCompound: boolean;
}

interface TaxOverview {
  activeTaxRates: number;
  totalTaxRates: number;
  activeExemptions: number;
  expiringExemptions: number;
  mtdTax: number;
  ytdTax: number;
  taxByType: Array<{ taxType: string; amount: number }>;
}

/**
 * Stat card component
 */
function StatCard({
  label,
  value,
  subValue,
  icon,
  color = 'steel',
}: {
  label: string;
  value: string | number;
  subValue?: string;
  icon: React.ReactNode;
  color?: 'steel' | 'amber' | 'success' | 'warning';
}) {
  const borderColors = {
    steel: 'border-steel',
    amber: 'border-amber',
    success: 'border-success-green',
    warning: 'border-warning-amber',
  };

  const textColors = {
    steel: 'text-steel',
    amber: 'text-amber',
    success: 'text-success-green',
    warning: 'text-warning-amber',
  };

  return (
    <div className="relative stat-card">
      <div className={`absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 ${borderColors[color]}`} />
      <div className={`absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 ${borderColors[color]}`} />
      <div className={`absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 ${borderColors[color]}`} />
      <div className={`absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 ${borderColors[color]}`} />

      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="flex items-center space-x-2 mb-2">
          <div className={textColors[color]}>{icon}</div>
          <span className="text-[10px] uppercase tracking-[0.15em] text-text-secondary font-mono">
            {label}
          </span>
        </div>
        <div className={`text-2xl font-mono font-bold ${textColors[color]}`} style={{ textShadow: '0 0 10px currentColor' }}>
          {value}
        </div>
        {subValue && (
          <div className="text-xs text-text-tertiary font-mono mt-1">{subValue}</div>
        )}
      </div>
    </div>
  );
}

/**
 * Tax Rates Page Component
 */
const TaxRatesPage = () => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [overview, setOverview] = useState<TaxOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    fetchData();
  }, [showInactive]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [ratesRes, overviewRes] = await Promise.all([
        fetch(`${API_URL}/api/tax/rates?includeInactive=${showInactive}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/tax/overview`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      if (ratesRes.ok) {
        const data = await ratesRes.json();
        setTaxRates(data.taxRates || []);
      }

      if (overviewRes.ok) {
        const data = await overviewRes.json();
        setOverview(data.overview);
      }
    } catch (error) {
      console.error('Failed to fetch tax data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (taxRateId: string) => {
    if (!confirm('Are you sure you want to deactivate this tax rate?')) return;

    try {
      const response = await fetch(`${API_URL}/api/tax/rates/${taxRateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Failed to delete tax rate:', error);
    }
  };

  const formatRate = (rate: TaxRate) => {
    switch (rate.rateType) {
      case 'PERCENT':
        return `${rate.rate}%`;
      case 'PER_GALLON':
        return `$${rate.rate}/gal`;
      case 'PER_UNIT':
        return `$${rate.rate}/unit`;
      case 'FLAT':
        return `$${rate.rate} flat`;
      default:
        return rate.rate.toString();
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatJurisdiction = (rate: TaxRate) => {
    const parts = [rate.city, rate.county, rate.state].filter(Boolean);
    return parts.join(', ') || rate.country || 'All';
  };

  // Filter and search
  const filteredRates = taxRates.filter((rate) => {
    const matchesSearch =
      rate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rate.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rate.state && rate.state.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = !filterType || rate.taxType === filterType;
    return matchesSearch && matchesType;
  });

  const taxTypes = [...new Set(taxRates.map((r) => r.taxType))];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-amber font-mono animate-pulse">Loading Tax Rates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-3xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Tax Management
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Configure tax rates, manage exemptions, and track tax collection
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={fetchData}
                className="flex items-center space-x-2 px-4 py-2 bg-steel/10 hover:bg-steel/20 border border-steel/30 text-steel font-mono text-sm uppercase tracking-wider transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
              <Link
                to="/settings/tax/new"
                className="flex items-center space-x-2 px-4 py-2 bg-amber hover:bg-amber/90 text-background font-mono text-sm uppercase tracking-wider transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Tax Rate</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard
            label="Active Rates"
            value={overview.activeTaxRates}
            subValue={`of ${overview.totalTaxRates} total`}
            icon={<Receipt className="w-4 h-4" />}
            color="steel"
          />
          <StatCard
            label="MTD Tax"
            value={formatCurrency(overview.mtdTax)}
            icon={<DollarSign className="w-4 h-4" />}
            color="amber"
          />
          <StatCard
            label="YTD Tax"
            value={formatCurrency(overview.ytdTax)}
            icon={<DollarSign className="w-4 h-4" />}
            color="success"
          />
          <StatCard
            label="Active Exemptions"
            value={overview.activeExemptions}
            icon={<CheckCircle className="w-4 h-4" />}
            color="steel"
          />
          <StatCard
            label="Expiring Soon"
            value={overview.expiringExemptions}
            subValue="next 30 days"
            icon={<AlertTriangle className="w-4 h-4" />}
            color={overview.expiringExemptions > 0 ? 'warning' : 'steel'}
          />
          <Link to="/settings/tax/exemptions" className="block">
            <StatCard
              label="Manage"
              value="Exemptions"
              icon={<Calendar className="w-4 h-4" />}
              color="steel"
            />
          </Link>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
            <input
              type="text"
              placeholder="Search tax rates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-panel-dark border border-panel-border text-text-primary font-mono text-sm placeholder-text-tertiary focus:border-amber focus:outline-none w-64"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-10 pr-8 py-2 bg-panel-dark border border-panel-border text-text-primary font-mono text-sm focus:border-amber focus:outline-none appearance-none"
            >
              <option value="">All Types</option>
              {taxTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>
        <label className="flex items-center space-x-2 text-text-secondary text-sm font-mono cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 bg-panel-dark border border-panel-border text-amber focus:ring-amber"
          />
          <span>Show Inactive</span>
        </label>
      </div>

      {/* Tax Rates Table */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-background/50 border-b border-panel-border">
              <tr>
                <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                  Tax Rate
                </th>
                <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                  Type
                </th>
                <th className="px-4 py-3 text-right text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                  Rate
                </th>
                <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                  Jurisdiction
                </th>
                <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                  Applies To
                </th>
                <th className="px-4 py-3 text-center text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRates.length > 0 ? (
                filteredRates.map((rate) => (
                  <tr
                    key={rate.id}
                    className={`border-b border-panel-border hover:bg-panel-border/20 transition-colors ${
                      !rate.isActive ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-mono text-sm text-text-primary">{rate.name}</div>
                      <div className="text-xs text-text-tertiary">{rate.code}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-steel/10 text-steel text-xs font-mono uppercase rounded">
                        {rate.taxType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-amber text-sm">{formatRate(rate)}</span>
                      <div className="text-[10px] text-text-tertiary font-mono">{rate.rateType}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-3 h-3 text-text-tertiary" />
                        <span className="text-sm text-text-secondary font-mono">
                          {formatJurisdiction(rate)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {rate.productCategories.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {rate.productCategories.slice(0, 2).map((cat) => (
                            <span
                              key={cat}
                              className="px-1.5 py-0.5 bg-panel-border text-text-tertiary text-[10px] font-mono rounded"
                            >
                              {cat}
                            </span>
                          ))}
                          {rate.productCategories.length > 2 && (
                            <span className="text-[10px] text-text-tertiary">
                              +{rate.productCategories.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-text-tertiary font-mono">All Products</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {rate.isActive ? (
                        <CheckCircle className="w-4 h-4 text-success-green inline" />
                      ) : (
                        <XCircle className="w-4 h-4 text-error-red inline" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/settings/tax/${rate.id}`)}
                          className="p-1.5 text-steel hover:text-amber hover:bg-amber/10 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {rate.isActive && (
                          <button
                            onClick={() => handleDelete(rate.id)}
                            className="p-1.5 text-error-red hover:text-error-red hover:bg-error-red/10 rounded transition-colors"
                            title="Deactivate"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center space-y-2">
                      <Receipt className="w-8 h-8 text-text-tertiary" />
                      <span className="text-text-secondary font-mono text-sm">
                        {searchTerm || filterType
                          ? 'No tax rates match your search'
                          : 'No tax rates configured'}
                      </span>
                      {!searchTerm && !filterType && (
                        <Link
                          to="/settings/tax/new"
                          className="text-amber text-sm font-mono hover:underline"
                        >
                          Add your first tax rate
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tax by Type Chart */}
      {overview && overview.taxByType.length > 0 && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-steel" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-steel" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-steel" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-steel" />

          <div className="bg-panel-dark border border-panel-border p-4">
            <h3 className="text-sm font-display text-steel uppercase tracking-wide mb-4 pb-2 border-b border-panel-border">
              MTD Tax by Type
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {overview.taxByType.map((item) => (
                <div key={item.taxType} className="text-center p-3 bg-background/30 rounded">
                  <div className="text-xs text-text-tertiary font-mono uppercase mb-1">
                    {item.taxType}
                  </div>
                  <div className="text-lg font-mono text-amber">
                    {formatCurrency(item.amount)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxRatesPage;
